import Sentry from "@mirage/shared/sentry";
import { useEffect, useRef, useState } from "react";
import { useFssTeamInfo } from "./useFssTeamInfo";
import { useSelfServeFssSurveyResponses } from "./useSelfServeFssSurveyResponses";
import { useSelfServeOnboardingState } from "./useSelfServeOnboardingState";
import { useSelfServeOnboardingSteps } from "./useSelfServeOnboardingSteps";

export const useAdminSelfServeOnboardingSteps = (
  isAdmin: boolean,
  canProvisionDashFreemiumTeam = false,
) => {
  const {
    companyName: dashTeamName,
    companyLogo: dashTeamLogo,
    setCompanyName: setDashTeamName,
    setCompanyLogo: setDashTeamLogo,
    loading: loadingDashOnboardingState,
  } = useSelfServeOnboardingState();
  const {
    teamName: fssTeamName,
    teamLogo: fssTeamLogo,
    surveyResponses,
    loading: loadingFssTeamInfo,
  } = useFssTeamInfo(isAdmin);
  const { answeredProfilingQuestions } = useSelfServeFssSurveyResponses(
    isAdmin,
    canProvisionDashFreemiumTeam,
    surveyResponses,
  );

  const [canSkipCompanyInfoStep, setCanSkipCompanyInfoStep] = useState(false);
  const [loading, setLoading] = useState(true);
  const hasHydrated = useRef(false);

  const {
    refreshSteps,
    onboardingSteps,
    setShowPrivacyModal,
    showPrivacyModal,
  } = useSelfServeOnboardingSteps(
    isAdmin,
    canSkipCompanyInfoStep,
    answeredProfilingQuestions,
  );

  useEffect(() => {
    const hydrateTeamInfo = async () => {
      if (loadingDashOnboardingState || loadingFssTeamInfo) {
        return;
      }
      if (!isAdmin || hasHydrated.current) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        hasHydrated.current = true;

        if (fssTeamName && !dashTeamName) {
          setDashTeamName(fssTeamName);
        }

        if (fssTeamLogo && !dashTeamLogo) {
          const dataUrl = `data:image/png;base64,${fssTeamLogo}`;
          setDashTeamLogo(dataUrl);
        }

        // Refresh steps only once and only if teamName is available
        if (fssTeamName) {
          refreshSteps();
          setCanSkipCompanyInfoStep(true);
        }
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setLoading(false);
      }
    };

    hydrateTeamInfo();
  }, [
    isAdmin,
    loadingDashOnboardingState,
    dashTeamName,
    dashTeamLogo,
    setDashTeamName,
    setDashTeamLogo,
    refreshSteps,
    loadingFssTeamInfo,
    fssTeamName,
    fssTeamLogo,
  ]);

  return {
    steps: onboardingSteps,
    loading: loading || loadingFssTeamInfo || loadingDashOnboardingState,
    refreshSteps,
    setShowPrivacyModal,
    showPrivacyModal,
  };
};
